import { useState, useRef, useCallback, useEffect } from 'react';
import { getSubscriptionStatus } from '@/api/subscription';
import { SubscriptionStatus } from '@/types/subscription';
import { PollingStatus } from '@/types/order';
import { SUBSCRIPTION_POLLING_CONNECTION_LOST_GRACE_MS, SUBSCRIPTION_POLLING_INTERVAL_MS } from '@/constants/chargebee';
import type { ApiError } from '@/services/apiClient';
import { getApiErrorCode } from '@/utils/error';
import { logError, logInfo } from '@/utils/logger';

interface UseSubscriptionPollingReturn {
  triggerSubscriptionPolling: (subscriptionId: string) => void;
  stop: () => void;
  statusSubscriptionPolling: PollingStatus;
  error?: string;
  /**
   * Gateway `errorCode` from a failed poll, kept alongside `error` because the
   * latter is flattened to a display string and so cannot tell an ended
   * session from any other failure. See {@link useOrderPolling}.
   */
  errorCode?: string;
}

const CONNECTION_LOST_REASON = 'Connection lost';

/**
 * A subscription in one of these states will never turn ACTIVE for this
 * checkout, so polling on (v2 only ever stops on ACTIVE) would spin the
 * processing overlay forever. `failed_charged` is payment-succeeded / award-failed.
 * PENDING/PAUSED are still in flight and keep polling, exactly like a
 * non-terminal order state in {@link useOrderPolling}.
 */
const TERMINAL_FAILURE_STATUSES = new Set<SubscriptionStatus>([
  SubscriptionStatus.CANCELED,
  SubscriptionStatus.EXPIRED,
  SubscriptionStatus.FAILED_CHARGED,
]);

/** fetch throws a status-less TypeError on network failure; HTTP errors carry `status`. */
const isNetworkError = (error: unknown): boolean => !(error as ApiError)?.status;

/**
 * Polls `GET /checkout/subscriptions/{id}` until the subscription is ACTIVE.
 * Modelled on {@link useOrderPolling} (same PollingStatus contract, so
 * SuccessScreen gating works unchanged), with v2's semantics ported: 2500ms
 * cadence and an outage tolerated for up to 60s before giving up. Non-network
 * errors fail immediately.
 */
export const useSubscriptionPolling = (): UseSubscriptionPollingReturn => {
  const [state, setState] = useState<{ status: PollingStatus; error?: string; errorCode?: string }>({
    status: PollingStatus.IDLE,
  });
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Timestamp of the FIRST unanswered poll of the current outage (v2's
  // `connectionLostTimestamp`), so the grace window measures the outage itself.
  const connectionLostAtRef = useRef<number | null>(null);

  const stop = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const poll = useCallback(
    async (subscriptionId: string) => {
      try {
        const response = await getSubscriptionStatus(subscriptionId);
        connectionLostAtRef.current = null;
        if (response?.status === SubscriptionStatus.ACTIVE) {
          logInfo('Subscription polling succeeded', { subscriptionId });
          stop();
          setState({ status: PollingStatus.SUCCESS });
          return;
        }
        if (response?.status && TERMINAL_FAILURE_STATUSES.has(response.status)) {
          stop();
          const reason = `Subscription ${response.status}`;
          logError('Subscription polling failed', undefined, { subscriptionId, reason });
          setState({ status: PollingStatus.FAILED, error: reason });
        }
      } catch (error) {
        if (isNetworkError(error)) {
          connectionLostAtRef.current ??= Date.now();
          if (Date.now() - connectionLostAtRef.current < SUBSCRIPTION_POLLING_CONNECTION_LOST_GRACE_MS) {
            return; // transient — keep polling until the outage outlives the grace window
          }
        }
        stop();
        const reason = isNetworkError(error)
          ? CONNECTION_LOST_REASON
          : error instanceof Error
            ? error.message
            : 'An error occurred';
        logError('Subscription polling failed', error, { subscriptionId, reason });
        setState({ status: PollingStatus.FAILED, error: reason, errorCode: getApiErrorCode(error) });
      }
    },
    [stop]
  );

  const trigger = useCallback(
    (subscriptionId: string) => {
      stop();
      connectionLostAtRef.current = null;
      setState({ status: PollingStatus.POLLING });
      logInfo('Subscription polling started', { subscriptionId, fetchIntervalMs: SUBSCRIPTION_POLLING_INTERVAL_MS });
      poll(subscriptionId);
      pollingIntervalRef.current = setInterval(() => poll(subscriptionId), SUBSCRIPTION_POLLING_INTERVAL_MS);
    },
    [poll, stop]
  );

  useEffect(() => {
    return () => stop();
  }, [stop]);

  return {
    triggerSubscriptionPolling: trigger,
    stop,
    statusSubscriptionPolling: state.status,
    error: state.error,
    errorCode: state.errorCode,
  };
};
